/* eslint-disable no-await-in-loop */
import { AuthV3ErrorClassification } from '@am3nd-org/pointcraft-types';

import {
  isDefinitiveDeviceFailure,
  classifyAuthOperationError,
  isTransientAuthOperationFailure,
} from 'src/auth/errors/auth-operation-error';

import { BusinessDeviceV3Error } from './api';
import { BusinessDeviceIdentityError } from './token-claims';

export type AuthV3ErrorDisposition = {
  classification: string;
  retryable: boolean;
  clearDeviceSession: boolean;
  requiresReauthentication: boolean;
  retryAfterMs?: number;
  supportReference?: string;
};

const DEFINITIVE_SESSION_FAILURES = new Set<string>([
  AuthV3ErrorClassification.DEVICE_NOT_FOUND,
  AuthV3ErrorClassification.DEVICE_REVOKED,
  AuthV3ErrorClassification.DEVICE_KEY_MISMATCH,
]);

const SECURITY_OR_OWNERSHIP_FAILURES = new Set<string>([
  AuthV3ErrorClassification.DEVICE_OWNED_BY_OTHER_BUSINESS,
  AuthV3ErrorClassification.DEVICE_TYPE_CONFLICT,
  AuthV3ErrorClassification.TERMINAL_OWNED_BY_OTHER_BUSINESS,
  AuthV3ErrorClassification.TERMINAL_INACTIVE,
  AuthV3ErrorClassification.PAIRING_PROOF_INVALID,
  AuthV3ErrorClassification.PAIRING_PROOF_MISMATCH,
  'TOKEN_CLAIMS_MISMATCH',
]);

export const classifyAuthV3Error = (error: unknown): AuthV3ErrorDisposition => {
  if (error instanceof BusinessDeviceIdentityError) {
    return {
      classification: error.errorCode,
      retryable: false,
      clearDeviceSession: false,
      requiresReauthentication: false,
    };
  }

  if (!(error instanceof BusinessDeviceV3Error)) {
    const failure = classifyAuthOperationError(error);
    if (failure.kind !== 'UNKNOWN') {
      return {
        classification: failure.code ?? failure.kind,
        retryable: isTransientAuthOperationFailure(failure),
        clearDeviceSession: isDefinitiveDeviceFailure(failure),
        requiresReauthentication: failure.kind === 'AUTHENTICATION',
        retryAfterMs: failure.retryAfterMs,
        supportReference: failure.supportReference,
      };
    }
    return {
      classification: 'UNCLASSIFIED',
      retryable: false,
      clearDeviceSession: false,
      requiresReauthentication: false,
    };
  }

  const classification = error.errorCode || (error.isNetworkError ? 'NETWORK_ERROR' : 'UNKNOWN');
  const clearDeviceSession = DEFINITIVE_SESSION_FAILURES.has(classification);
  const requiresReauthentication = error.httpStatus === 401;
  const transientStatus =
    error.httpStatus === 408 || error.httpStatus === 429 || (error.httpStatus ?? 0) >= 500;
  const retryable =
    !clearDeviceSession &&
    !requiresReauthentication &&
    !SECURITY_OR_OWNERSHIP_FAILURES.has(classification) &&
    (error.isNetworkError ||
      error.isTimeout ||
      transientStatus ||
      classification === 'C-9858' ||
      classification === AuthV3ErrorClassification.TOKEN_MINT_TEMPORARY_FAILURE);

  return {
    classification,
    retryable,
    clearDeviceSession,
    requiresReauthentication,
    retryAfterMs: error.retryAfterMs,
    supportReference: error.supportReference,
  };
};

export const AUTH_V3_RETRY_DELAYS_MS = [1000, 2000, 4000] as const;

export const getAuthV3RetryDelayMs = (
  retryIndex: number,
  disposition: AuthV3ErrorDisposition,
  random = Math.random
): number => {
  if (disposition.retryAfterMs !== undefined) return disposition.retryAfterMs;
  const baseDelay = AUTH_V3_RETRY_DELAYS_MS[retryIndex] ?? AUTH_V3_RETRY_DELAYS_MS.at(-1)!;
  return Math.round(baseDelay * (0.8 + random() * 0.4));
};

export const waitForAuthV3Retry = (delayMs: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });

export const runWithAuthV3Retry = async <T>(operation: () => Promise<T>): Promise<T> => {
  for (let attempt = 0; attempt <= AUTH_V3_RETRY_DELAYS_MS.length; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      const disposition = classifyAuthV3Error(error);
      if (!disposition.retryable || attempt === AUTH_V3_RETRY_DELAYS_MS.length) throw error;
      await waitForAuthV3Retry(getAuthV3RetryDelayMs(attempt, disposition));
    }
  }
  throw new Error('Auth V3 retry loop ended unexpectedly');
};
